<template>
  <div>
    <div class="col-3">
      <label for="subsegment-select">
        {{ $t('reports.partsAvailability.filterTitle') }}:
      </label>
      <b-form-select
        id="subsegment-select"
        v-model="selected"
        :options="options"
        @change="getChartData"
        :disabled="loading"
        class="mb-3"
      >
      </b-form-select>
    </div>

    <div v-if="loading" class="loader">
      <b-spinner
        variant="primary"
        type="grow"
        :label="`${this.$t('reports.loadingLabel')}...`"
      ></b-spinner>
    </div>

    <div v-else-if="chartData.series.length === 0">
      <div class="text-center mt-5">
        <p>
          <font-awesome-icon
            :icon="['fas', 'folder-open']"
            style="margin-right: 20px; font-size: 35px;"
          />
        </p>
        <h5>
          {{ this.$t('genericMessages.noDataTitle') }}
        </h5>
        <p>
          {{ this.$t('genericMessages.noDataDescription') }}
        </p>
      </div>
    </div>

    <div v-else>
      <div class="row">
        <div class="col-12">
          <div class="px-3">
            <highcharts :options="chartOptions"></highcharts>
          </div>
        </div>
      </div>
      <div v-if="table.show" class="row">
        <div class="col-12">
          <b-table
            striped
            id="parts-availability-table"
            class="mt-3"
            :fields="table.fields"
            :items="table.items"
            :busy="table.loading"
            :per-page="table.perPage"
            :current-page="table.currentPage"
            @sort-changed="sortingChanged"
            responsive
          >
            <template v-slot:table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner type="grow" class="align-middle"></b-spinner>
              </div>
            </template>

            <template v-slot:cell(createdAt)="row">
              {{ dateFormat(new Date(row.item.createdAt)) }}
            </template>

            <template v-slot:cell(review)="row">
              <star-rating
                :show-rating="false"
                :star-size="30"
                :read-only="true"
                :rating="row.item.additionalQuestions[0].review"
              ></star-rating>
            </template>

            <template v-slot:cell(name)="row">
              <span>{{
                checkName(row.item.anonymousResponse, row.item.name)
              }}</span>
            </template>

            <template v-slot:cell(feedback)="row">
              <span style="max-width: 150px;">{{ row.item.feedback }}</span>
            </template>

            <template v-slot:cell(details)="row">
              <div class="table-icons">
                <router-link
                  target="_blank"
                  :to="`/treatments/${row.item._id}`"
                >
                  <font-awesome-icon :icon="['fas', 'search']" />
                </router-link>
              </div>
            </template>
          </b-table>

          <div class="mt-4 mb-4 footer-buttons-area">
            <b-pagination
              v-model="table.currentPage"
              :total-rows="table.items.length"
              :per-page="table.perPage"
              aria-controls="table-items"
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { lightFormat } from 'date-fns';
import starRating from 'vue-star-rating';
import api from '../../../../services/api';
import EventBus from '../../../../eventBus';

export default {
  name: 'PartsAvailability',

  components: { starRating },

  data: () => ({
    monthlyText: '',
    accumulatedText: '',
    userAuth: null,
    selected: null,
    chartOptions: {},
    chartData: {
      categories: [],
      series: [],
      max: [],
    },
    type: 'column',
    chartMax: null,
    monthly: false,
    accumulated: true,
    loading: false,
    options: [],
    question:
      'Como você avalia a disponibilidade de peças na concessionária para realização do serviço?',
    table: {
      fields: [],
      items: [],
      show: false,
      loading: false,
      perPage: 10,
      currentPage: 1,
    },
  }),

  computed: {
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('companies', ['myMetric']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },

  watch: {
    actionGroupData: async function watchActionGroupData() {
      await this.getChartData();
    },

    monthly: async function watchMonthly() {
      if (this.monthly) {
        this.monthlyText = `- ${this.$t('reports.monthlyLabel')}`;
      } else {
        this.monthlyText = `- ${this.$t('reports.dailyLabel')}`;
      }

      await this.getChartData();
    },

    accumulated: async function watchAccumulated() {
      if (!this.accumulated) {
        this.accumulatedText = '';
      } else {
        this.accumulatedText = this.$t('reports.accLabel');
      }

      await this.getChartData();
    },

    calendarDates: async function watchCalendar() {
      await this.getChartData();
    },

    myActionId: async function watchActionId() {
      await this.getChartData();
    },

    myActionsIds: async function watchActionsIds() {
      await this.getChartData();
    },

    myIh1: async function watchih1() {
      await this.getChartData();
    },

    myHierarchies1: async function watchMyHierarchies1() {
      await this.getChartData();
    },

    myIh2: async function watchih2() {
      await this.getChartData();
    },

    myHierarchies2: async function watchMyHierarchies2() {
      await this.getChartData();
    },

    'indicatorData.value': async function watchActionId() {
      await this.getChartData();
    },
    'indicatorData.period': async function watchIndicatorPeriod() {
      await this.getChartData();
    },

    '$i18n.locale': function watchLocale() {
      this.changeLabels();
    },
  },

  methods: {
    async getChartData() {
      this.table.show = false;
      this.loading = true;

      const response = await api.get(
        `/reports/additional-question-chart/${this.userAuth.companyId}/REVIEWS`,
        {
          headers: { 'x-access-token': this.userAuth.token },
          params: {
            actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
            startDate: this.calendarDates.start,
            endDate: this.calendarDates.end,
            ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
            ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
            monthly: this.monthly,
            accumulated: this.accumulated,
            metric: this.myMetric,
            groupId: this.actionGroupData._id,
            question: this.question,
            indicator: 'subsegmento',
            indicatorValue: this.selected,
          },
        },
      );

      this.chartData = response.data;
      this.chartMax = response.data.max;

      this.renderChart();

      this.chartData.series.map((res) => {
        this.chartOptions.series[0].data.push({
          y: res.average,
          answerIds: res.answerIds,
        });
      });

      this.loading = false;
    },

    async handleSubSegmentIndicator() {
      const response = await api.get(
        `/filters/indicator-data/${this.userAuth.companyId}`,
        {
          headers: { 'x-access-token': this.userAuth.token },
          params: {
            indicatorValue: 'subsegmento',
            groupId: this.actionGroupData._id,
            actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
            ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
            ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          },
        },
      );

      this.options = [
        {
          value: null,
          text: this.$t('reports.partsAvailability.filterPlaceholder'),
        },
      ];

      response.data.forEach((el) => {
        this.options.push({ value: el.key, text: el.text });
      });
    },

    async renderChart() {
      this.chartOptions = {
        title: {
          align: 'left',
          text: `${this.$t('reports.partsAvailability.reportTitle')} ${
            this.monthlyText
          } ${this.accumulatedText}`,
          style: {
            color: '#5f6368',
          },
        },
        chart: {
          type: this.type,
          borderColor: 'rgba(0, 0, 0, 0.09)',
          style: {
            fontFamily:
              '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
            fontSize: '12px',
            width: '100%',
          },
        },
        xAxis: {
          categories: this.chartData.categories,
          visible: true,
          labels: {},
        },
        yAxis: {
          max: this.chartMax,
          min: 0,
          gridLineWidth: 0,
          labels: {
            enabled: false,
          },
          title: {
            text: '',
          },
          stackLabels: {
            enabled: true,
            fontWeight: 'bold',
          },
        },
        series: [
          {
            name: 'Score',
            data: [],
            cursor: 'pointer',
            point: {
              events: {
                click: (e) => this.handleClick(e),
              },
            },
          },
        ],
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              format: '{y:.2f}',
            },
          },
          line: {
            dataLabels: {
              enabled: true,
              format: '{y:.2f}',
            },
          },
          area: {
            dataLabels: {
              enabled: true,
              format: '{y:.2f}',
            },
          },
          series: {
            groupPadding: 0,
            pointPadding: 0.05,
            borderWidth: 0,
          },

          accessibility: {
            pointDescriptionFormatter: (point) => {
              function round(x) {
                return Math.round(x * 100) / 100;
              }
              return `${point.index + 1}, ${point.category}, ${
                point.y
              } millions, ${round(point.percentage)}, ${point.series.name}`;
            },
          },
        },
        tooltip: {
          pointFormat: '<span>{series.name}<b> {point.y:.2f}<b/>',
          shared: true,
        },
        colors: ['#4ce595'],
      };
    },

    async handleClick(e) {
      this.table.show = true;
      this.table.loading = true;

      const response = await api.put(
        '/parts-availability/answers',
        {
          answerIds: e.point.answerIds,
          question: this.question,
        },
        {
          headers: {
            'x-access-token': this.userAuth.token,
          },
        },
      );

      this.table.items = response.data;

      this.table.loading = false;
    },

    dateFormat(param) {
      const day = lightFormat(new Date(param), 'dd/MM/yyyy');
      const hour = lightFormat(new Date(param), 'HH:mm');
      return `${day} às ${hour}`;
    },

    checkName(anonymous, name) {
      if (anonymous) {
        return 'Anônimo';
      } else if (name) {
        return name;
      }
      return 'Sem Nome';
    },

    changeLabels() {
      this.options = [
        {
          value: null,
          text: this.$t('reports.partsAvailability.filterPlaceholder'),
        },
      ];

      this.table.fields = [
        {
          key: 'controlId',
          label: 'ID',
        },
        {
          key: 'createdAt',
          label: this.$t('reports.partsAvailability.table.date'),
        },
        {
          key: 'review',
          label: this.$t('reports.partsAvailability.table.review'),
          sortable: true,
        },
        {
          key: 'name',
          label: this.$t('reports.partsAvailability.table.customer'),
        },
        {
          key: 'feedback',
          label: this.$t('reports.partsAvailability.table.comment'),
        },
        {
          key: 'details',
          label: this.$t('reports.partsAvailability.table.details'),
        },
      ];
    },

    sortingChanged(ctx) {
      if (ctx.sortBy === 'review') {
        if (ctx.sortDesc) {
          this.table.items.sort((a, b) => {
            if (
              a.additionalQuestions[0].review > b.additionalQuestions[0].review
            ) {
              return 1;
            }

            if (
              a.additionalQuestions[0].review < b.additionalQuestions[0].review
            ) {
              return -1;
            }

            return 0;
          });
        } else {
          this.table.items.sort((a, b) => {
            if (
              a.additionalQuestions[0].review < b.additionalQuestions[0].review
            ) {
              return 1;
            }

            if (
              a.additionalQuestions[0].review > b.additionalQuestions[0].review
            ) {
              return -1;
            }

            return 0;
          });
        }
      }
    },
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    this.monthlyText = `- ${this.$t('reports.dailyLabel')}`;

    this.accumulatedText = this.$t('reports.accLabel');

    this.changeLabels();
  },

  async mounted() {
    this.loading = true;

    EventBus.$on('data-source', (checked) => {
      this.monthly = checked;
    });

    EventBus.$on('accumulated-report', (checked) => {
      this.accumulated = checked;
    });

    EventBus.$on('switch-chart', (checked) => {
      this.type = checked;
    });

    await Promise.all([this.getChartData(), this.handleSubSegmentIndicator()]);

    this.loading = false;
  },

  beforeDestroy() {
    EventBus.$off('switch-chart');
    EventBus.$off('data-source');
    EventBus.$off('accumulated-report');
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';

.loader {
  width: 100%;
  height: 200px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.table-icons {
  color: $primary;
  padding-left: 25px;
  font-size: 20px;
  svg {
    &:hover {
      color: #46b41e;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/parts-availability/index.vue